import React, {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { InputBox } from '../input/Input';

type DebounceFunction = (value: string) => void;
function debounce(
	debounceFunction: DebounceFunction,
	milliseconds: number
): DebounceFunction {
	let timer: ReturnType<typeof setTimeout> | null = null;

	return (value: string) => {
		if (timer) {
			clearTimeout(timer);
		}
		timer = setTimeout(() => debounceFunction(value), milliseconds);
	};
}

type Value = string;

type SearchBoxOwnProps = {
	value: Value;
	onChange: (value: Value) => void;
	placeholder?: string;
	/**
	 * Delay before calling `onChange` after typing. Default 300.
	 * Use a low value (e.g. 20) for near-immediate updates; `0` disables debouncing (fully controlled).
	 */
	debounceMs?: number;
	className?: string;
};

export type ISearchBoxProps = SearchBoxOwnProps &
	Omit<React.ComponentProps<typeof InputBox>, 'value' | 'onChange' | 'variant'>;

const SearchBoxImmediate = forwardRef<HTMLInputElement, ISearchBoxProps>(
	(
		{
			value,
			onChange,
			className,
			placeholder,
			debounceMs: _debounceMs,
			...inputProps
		},
		ref
	) => {
		const handleChange = useCallback(
			(event: React.ChangeEvent<HTMLInputElement>) => {
				onChange(event.target.value);
			},
			[onChange]
		);

		return (
			<InputBox
				ref={ref}
				variant="search"
				value={value}
				placeholder={placeholder}
				onChange={handleChange}
				className={className}
				{...inputProps}
			/>
		);
	}
);

SearchBoxImmediate.displayName = 'SearchBoxImmediate';

const SearchBoxDebounced = forwardRef<HTMLInputElement, ISearchBoxProps>(
	(
		{
			value,
			onChange,
			className,
			placeholder,
			debounceMs = 300,
			...inputProps
		},
		ref
	) => {
		const [localValue, setLocalValue] = useState<Value>(value);

		useEffect(() => {
			setLocalValue(value);
		}, [value]);

		const debouncedOnChange = useMemo(
			() => debounce((val: Value) => onChange(val), debounceMs),
			[onChange, debounceMs]
		);

		const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			const next = event.target.value;
			setLocalValue(next);
			debouncedOnChange(next);
		};

		return (
			<InputBox
				ref={ref}
				variant="search"
				value={localValue}
				placeholder={placeholder}
				onChange={handleChange}
				className={className}
				{...inputProps}
			/>
		);
	}
);

SearchBoxDebounced.displayName = 'SearchBoxDebounced';

const SearchBox = forwardRef<HTMLInputElement, ISearchBoxProps>(
	({ debounceMs = 300, ...rest }, ref) => {
		if (debounceMs <= 0) {
			return <SearchBoxImmediate ref={ref} debounceMs={debounceMs} {...rest} />;
		}

		return <SearchBoxDebounced ref={ref} debounceMs={debounceMs} {...rest} />;
	}
);

SearchBox.displayName = 'SearchBox';

export { SearchBox };
